import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FC, memo, useCallback, useMemo } from 'react';
import { TruckDriverConstraintsSelector } from 'src/components/ui/shipmentOrders/TruckDriverConstraintsSelector';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  DesignatedTruckCarModelOptions,
  DesignatedTruckFeatureOptions,
  DesignatedTruckFloorSpecificationOptions,
  DesignatedTruckKlassOptions,
  DesignatedTruckLoadingPlatformHeightOptions,
  DesignatedTruckLoadingPlatformLengthOptions,
  DesignatedTruckLoadingPlatformWidthOptions,
  TruckDriverIdAndName,
} from 'src/entities/orderEntity';
import { ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import {
  ShipmentOrderWaypointTimeEntity,
} from 'src/entities/ShipmentOrderResponse.entity';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { useTrucksQuery } from 'src/hooks/useTrucks.query';
import {
  mergeConstraintSelectionsByMasterOrder,
  mergeItemCanBeMixedLabel,
  mergeLegStatsDisplay,
  mergeUniqueFieldValues,
  mergeUniqueStrings,
} from 'src/utils/laneBasicInfoMerge';
import { getEarliestDateFormatted } from 'src/utils/shipmentLegRunItems';

import type { LaneBasicInfoDraft } from 'src/utils/buildLaneBasicInfoShipmentPatch';

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing: boolean;
  startOn: string;
  basicInfoDraft: LaneBasicInfoDraft | null;
  setBasicInfoDraft: React.Dispatch<React.SetStateAction<LaneBasicInfoDraft | null>>;
  markBasicInfoDirty: () => void;
};

const WaypointTimesBlock: FC<{
  title: string;
  times: ShipmentOrderWaypointTimeEntity[];
  loadingOnFallback: string;
  isEditing: boolean;
  onChange: (next: ShipmentOrderWaypointTimeEntity[]) => void;
  onDirty: () => void;
}> = memo(({ title, times, loadingOnFallback, isEditing, onChange, onDirty }) => {
  const updateRow = useCallback((index: number, patch: Partial<ShipmentOrderWaypointTimeEntity>) => {
    const next = [...times];
    next[index] = { ...next[index], ...patch };
    onChange(next);
    onDirty();
  }, [times, onChange, onDirty]);

  const appendRow = useCallback(() => {
    if (times.length >= 3) return;
    onChange([...times, { startAt: '', endAt: '', stayingSeconds: null }]);
    onDirty();
  }, [times, onChange, onDirty]);

  const removeRow = useCallback((index: number) => {
    if (times.length <= 1) return;
    onChange(times.filter((_, i) => i !== index));
    onDirty();
  }, [times, onChange, onDirty]);

  return (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{title}</Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {times.map((field, index) => (
          <Box key={field.id ?? `row-${index}`} display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {isEditing ? (
              <Box display="flex" gap={2} alignItems="center" flex={1}>
                <DatePicker
                  label="日付"
                  value={new Date(field.date ?? loadingOnFallback)}
                  onChange={(date) => {
                    const startTime = datetimeDecorator.toHourMinutes(new Date(field.startAt || loadingOnFallback));
                    const endTime = datetimeDecorator.toHourMinutes(new Date(field.endAt || loadingOnFallback));
                    const newDate = datetimeDecorator.toYyyyMmDd(new Date(date || loadingOnFallback));
                    updateRow(index, {
                      date: newDate,
                      startAt: startTime ? `${newDate}T${startTime}:00` : field.startAt,
                      endAt: endTime ? `${newDate}T${endTime}:00` : field.endAt,
                    });
                  }}
                  format="yyyy/MM/dd"
                  slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                />
                <TextField
                  type="time"
                  label="開始時間"
                  size="small"
                  sx={{ flex: 1 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={field.startAt ? datetimeDecorator.toHourMinutes(new Date(field.startAt)) : ''}
                  onChange={(e) => {
                    const dateValue = field.date ?? loadingOnFallback;
                    const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue));
                    updateRow(index, { startAt: `${currentDate}T${e.target.value}:00` });
                  }}
                />
                <Typography>〜</Typography>
                <TextField
                  type="time"
                  label="終了時間"
                  size="small"
                  sx={{ flex: 1 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={field.endAt ? datetimeDecorator.toHourMinutes(new Date(field.endAt)) : ''}
                  onChange={(e) => {
                    const dateValue = field.date ?? loadingOnFallback;
                    const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue));
                    updateRow(index, { endAt: `${currentDate}T${e.target.value}:00` });
                  }}
                />
                <IconButton
                  size="small"
                  color="error"
                  disabled={times.length >= 3}
                  onClick={appendRow}
                >
                  <AddIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  disabled={times.length <= 1}
                  onClick={() => removeRow(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="body1" fontWeight="medium">
                  {datetimeDecorator.toYyyyMmDdHhMm(new Date(field.startAt))}
                </Typography>
                <Typography variant="body1">〜</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {datetimeDecorator.toYyyyMmDdHhMm(new Date(field.endAt))}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
});

/**
 * レーン詳細 - 基本情報タブ（案件管理の伝票詳細と同構成。N 件伝票はマージ表示・編集はレーン単位ドラフト）
 */
const LaneDetailBasicInfo: FC<Props> = memo(({
  detail,
  isEditing,
  startOn,
  basicInfoDraft,
  setBasicInfoDraft,
  markBasicInfoDirty,
}) => {
  const orders = detail.shipmentOrders;
  const { data: truckData } = useTrucksQuery(startOn, { enabled: !!startOn });
  const { data: driverData } = useDriversQuery(startOn, { enabled: !!startOn });

  const trucksOptions = useMemo(() => (truckData ?? []).map((t) => ({
    id: t.id,
    name: t.licensePlateValue,
  })), [truckData]);

  const driversOptions = useMemo(() => (driverData ?? []).map((d) => ({
    id: d.id,
    name: d.name,
  })), [driverData]);

  const loadingNames = mergeUniqueStrings(orders.map((o) => o.shipmentOrderLoadingWaypoint.name));
  const loadingAddresses = mergeUniqueStrings(orders.map((o) => o.shipmentOrderLoadingWaypoint.address));
  const unloadingNames = mergeUniqueStrings(orders.map((o) => o.shipmentOrderUnloadingWaypoint.name));
  const unloadingAddresses = mergeUniqueStrings(orders.map((o) => o.shipmentOrderUnloadingWaypoint.address));

  const codesDisplay = mergeUniqueStrings(orders.map((o) => o.code));
  const registrantsDisplay = mergeUniqueStrings(
    orders.map((o) => o.registeredLicense?.licenseProfile?.personName || o.registeredLicense?.email || ''),
  );

  const allAllowedTruckIds = [...new Set(orders.flatMap((o) => o.allowedTruckIds ?? []))];
  const allDeniedDriverIds = [...new Set(orders.flatMap((o) => o.deniedDriverIds ?? []))];

  const mergedDesignatedTruckKlasses = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckKlasses, DesignatedTruckKlassOptions),
    [orders],
  );
  const mergedDesignatedTruckCarModels = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckCarModels, DesignatedTruckCarModelOptions),
    [orders],
  );
  const mergedDesignatedTruckLoadingPlatformHeights = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformHeights,
      DesignatedTruckLoadingPlatformHeightOptions,
    ),
    [orders],
  );
  const mergedDesignatedTruckLoadingPlatformWidths = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformWidths,
      DesignatedTruckLoadingPlatformWidthOptions,
    ),
    [orders],
  );
  const mergedDesignatedTruckLoadingPlatformLengths = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformLengths,
      DesignatedTruckLoadingPlatformLengthOptions,
    ),
    [orders],
  );
  const mergedDesignatedTruckFloorSpecifications = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFloorSpecifications,
      DesignatedTruckFloorSpecificationOptions,
    ),
    [orders],
  );
  const mergedDesignatedTruckFeatures = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckFeatures, DesignatedTruckFeatureOptions),
    [orders],
  );

  const draftAllowedTrucks = useMemo((): TruckDriverIdAndName[] => {
    if (!basicInfoDraft) return [];
    return basicInfoDraft.allowedTruckIds
      .map((id) => trucksOptions.find((t) => t.id === id))
      .filter((t): t is TruckDriverIdAndName => t != null);
  }, [basicInfoDraft, trucksOptions]);

  const draftDeniedDrivers = useMemo((): TruckDriverIdAndName[] => {
    if (!basicInfoDraft) return [];
    return basicInfoDraft.deniedDriverIds
      .map((id) => driversOptions.find((d) => d.id === id))
      .filter((d): d is TruckDriverIdAndName => d != null);
  }, [basicInfoDraft, driversOptions]);

  const handleChangeAllowedTrucks = useCallback(
    (_event: unknown, value: TruckDriverIdAndName[]) => {
      setBasicInfoDraft((p) => (p ? { ...p, allowedTruckIds: value.map((t) => t.id) } : p));
      markBasicInfoDirty();
    },
    [setBasicInfoDraft, markBasicInfoDirty],
  );

  const handleChangeDeniedDrivers = useCallback(
    (_event: unknown, value: TruckDriverIdAndName[]) => {
      setBasicInfoDraft((p) => (p ? { ...p, deniedDriverIds: value.map((d) => d.id) } : p));
      markBasicInfoDirty();
    },
    [setBasicInfoDraft, markBasicInfoDirty],
  );

  const loadingOnFallback = basicInfoDraft
    ? getEarliestDateFormatted(basicInfoDraft.loadingWaypointTimes)
    : getEarliestDateFormatted(orders[0]?.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? []);

  const unloadingOnFallback = basicInfoDraft
    ? getEarliestDateFormatted(basicInfoDraft.unloadingWaypointTimes)
    : getEarliestDateFormatted(orders[0]?.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? []);

  if (!basicInfoDraft) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">基本情報を読み込んでいます…</Typography>
        </CardContent>
      </Card>
    );
  }

  const draft = basicInfoDraft;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>基本情報</Typography>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            システム情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">伝票No</Typography>
              <Typography variant="body1" fontWeight="medium">{codesDisplay}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">案件登録者</Typography>
              <Typography variant="body1" fontWeight="medium">{registrantsDisplay}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">レーンID</Typography>
              <Typography variant="body1" fontWeight="medium">{detail.id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">伝票数 / LEG数</Typography>
              <Typography variant="body1" fontWeight="medium">
                {detail.shipmentOrderCount}件 / {detail.legCount}件
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'blue.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            荷主情報
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {orders.map((shipmentOrder) => (
              <Box
                key={shipmentOrder.id}
                sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
              >
                <Typography variant="body1" fontWeight="bold" mb={1}>{shipmentOrder.shipperName}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, fontSize: '0.875rem' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">荷主担当者</Typography>
                    <Typography variant="body2">{shipmentOrder.shipperContactPerson}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">連絡先</Typography>
                    <Typography variant="body2">
                      TEL: {shipmentOrder.shipperPhoneNumber}<br />
                      MAIL: {shipmentOrder.shipperEmailAddress}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">配送形態</Typography>
                    <Typography variant="body2">{shipmentOrder.deliveryMethod}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'indigo.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            区間情報
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">区間種別（積地 → 降地）</Typography>
          <Typography variant="body1" fontWeight="medium">{mergeLegStatsDisplay(orders)}</Typography>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            積地情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積地名称</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingNames}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積地住所</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingAddresses}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積日</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingOnFallback || '—'}</Typography>
            </Box>
            <WaypointTimesBlock
              title="時間枠"
              times={draft.loadingWaypointTimes}
              loadingOnFallback={loadingOnFallback || detail.loadingOn}
              isEditing={isEditing}
              onChange={(next) => setBasicInfoDraft((p) => (p ? { ...p, loadingWaypointTimes: next } : p))}
              onDirty={markBasicInfoDirty}
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={draft.loadingStayingMinutes === '' ? '' : draft.loadingStayingMinutes}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0;
                    setBasicInfoDraft((p) => {
                      if (!p) return p;
                      return { ...p, loadingStayingMinutes: v };
                    });
                    markBasicInfoDirty();
                  }}
                  slotProps={{
                    input: { endAdornment: <Typography variant="body2">分</Typography> },
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  {draft.loadingStayingMinutes === '' || draft.loadingStayingMinutes === undefined
                    ? '—'
                    : `${draft.loadingStayingMinutes}分`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            降地情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降地名称</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingNames}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降地住所</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingAddresses}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降日</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingOnFallback || '—'}</Typography>
            </Box>
            <WaypointTimesBlock
              title="時間枠"
              times={draft.unloadingWaypointTimes}
              loadingOnFallback={unloadingOnFallback || detail.loadingOn}
              isEditing={isEditing}
              onChange={(next) => setBasicInfoDraft((p) => (p ? { ...p, unloadingWaypointTimes: next } : p))}
              onDirty={markBasicInfoDirty}
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={draft.unloadingStayingMinutes === '' ? '' : draft.unloadingStayingMinutes}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0;
                    setBasicInfoDraft((p) => {
                      if (!p) return p;
                      return { ...p, unloadingStayingMinutes: v };
                    });
                    markBasicInfoDirty();
                  }}
                  slotProps={{
                    input: { endAdornment: <Typography variant="body2">分</Typography> },
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  {draft.unloadingStayingMinutes === '' || draft.unloadingStayingMinutes === undefined
                    ? '—'
                    : `${draft.unloadingStayingMinutes}分`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'teal.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            貨物条件
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">貨物種別</Typography>
              <Typography variant="body1" fontWeight="medium">
                {mergeUniqueFieldValues(orders, (o) => o.itemKlass)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">荷扱い</Typography>
              <Typography variant="body1" fontWeight="medium">
                {mergeUniqueFieldValues(orders, (o) => o.itemHandlingOfCargoStyle)}
              </Typography>
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" color="text.secondary">積み合わせ可否</Typography>
                <Tooltip title="積み合わせ不可の場合は、同じ荷主・同じ貨物種別の荷物としか運べません">
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <Typography variant="body1" fontWeight="medium">{mergeItemCanBeMixedLabel(orders)}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'orange.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            備考
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              value={draft.memo}
              onChange={(e) => {
                setBasicInfoDraft((p) => (p ? { ...p, memo: e.target.value } : p));
                markBasicInfoDirty();
              }}
              placeholder="備考を入力してください"
            />
          ) : (
            <Typography variant="body1" fontWeight="medium" sx={{ minHeight: '72px' }}>
              {draft.memo || '—'}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'lime.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            任意項目情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {draft.customInputFieldEntries.map((entry, index) => (
              <Box key={entry.key}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{entry.key}</Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label={entry.key}
                    value={entry.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBasicInfoDraft((p) => {
                        if (!p) return p;
                        const next = [...p.customInputFieldEntries];
                        next[index] = { ...next[index], value: val };
                        return { ...p, customInputFieldEntries: next };
                      });
                      markBasicInfoDirty();
                    }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="medium">{entry.value || '未入力'}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'purple.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            制約情報
          </Typography>
          <Box display="flex" flexDirection="column" gap={4}>
            {/* 指定車両タイプ */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車両タイプ</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckKlassOptions.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckKlasses || []).includes(type)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckKlasses || [])];
                              if (e.target.checked) {
                                if (!cur.includes(type)) cur.push(type);
                              } else {
                                const i = cur.indexOf(type);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckKlasses: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={type}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckKlasses} />
              )}
            </Box>

            {/* 指定車種 */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車種</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckCarModelOptions.map((model) => (
                    <FormControlLabel
                      key={model}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckCarModels || []).includes(model)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckCarModels || [])];
                              if (e.target.checked) {
                                if (!cur.includes(model)) cur.push(model);
                              } else {
                                const i = cur.indexOf(model);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckCarModels: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={model}
                      sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content', flexShrink: 0, mb: 0 }}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckCarModels} />
              )}
            </Box>

            {/* 荷台高さ */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台高さ</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckLoadingPlatformHeightOptions.map((height) => (
                    <FormControlLabel
                      key={height}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckLoadingPlatformHeights || []).includes(height)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckLoadingPlatformHeights || [])];
                              if (e.target.checked) {
                                if (!cur.includes(height)) cur.push(height);
                              } else {
                                const i = cur.indexOf(height);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckLoadingPlatformHeights: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={height}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckLoadingPlatformHeights} />
              )}
            </Box>

            {/* 荷台幅 */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台幅</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckLoadingPlatformWidthOptions.map((width) => (
                    <FormControlLabel
                      key={width}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckLoadingPlatformWidths || []).includes(width)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckLoadingPlatformWidths || [])];
                              if (e.target.checked) {
                                if (!cur.includes(width)) cur.push(width);
                              } else {
                                const i = cur.indexOf(width);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckLoadingPlatformWidths: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={width}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckLoadingPlatformWidths} />
              )}
            </Box>

            {/* 荷台長さ */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台長さ</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckLoadingPlatformLengthOptions.map((length) => (
                    <FormControlLabel
                      key={length}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckLoadingPlatformLengths || []).includes(length)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckLoadingPlatformLengths || [])];
                              if (e.target.checked) {
                                if (!cur.includes(length)) cur.push(length);
                              } else {
                                const i = cur.indexOf(length);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckLoadingPlatformLengths: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={length}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckLoadingPlatformLengths} />
              )}
            </Box>

            {/* 床仕様 */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>床仕様</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckFloorSpecificationOptions.map((floor) => (
                    <FormControlLabel
                      key={floor}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckFloorSpecifications || []).includes(floor)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckFloorSpecifications || [])];
                              if (e.target.checked) {
                                if (!cur.includes(floor)) cur.push(floor);
                              } else {
                                const i = cur.indexOf(floor);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckFloorSpecifications: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={floor}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckFloorSpecifications} />
              )}
            </Box>

            {/* 装置 */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>装置</Typography>
              {isEditing ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {DesignatedTruckFeatureOptions.map((equipment) => (
                    <FormControlLabel
                      key={equipment}
                      control={(
                        <Checkbox
                          checked={(draft.designatedTruckFeatures || []).includes(equipment)}
                          onChange={(e) => {
                            setBasicInfoDraft((p) => {
                              if (!p) return p;
                              const cur = [...(p.designatedTruckFeatures || [])];
                              if (e.target.checked) {
                                if (!cur.includes(equipment)) cur.push(equipment);
                              } else {
                                const i = cur.indexOf(equipment);
                                if (i >= 0) cur.splice(i, 1);
                              }
                              return { ...p, designatedTruckFeatures: cur };
                            });
                            markBasicInfoDirty();
                          }}
                        />
                      )}
                      label={equipment}
                    />
                  ))}
                </Box>
              ) : (
                <ConstraintChipRow values={mergedDesignatedTruckFeatures} />
              )}
            </Box>

            {isEditing ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TruckDriverConstraintsSelector
                  truckData={trucksOptions}
                  driverData={driversOptions}
                  allowedTrucks={draftAllowedTrucks}
                  deniedDrivers={draftDeniedDrivers}
                  onChangeAllowedTrucks={handleChangeAllowedTrucks}
                  onChangeDeniedDrivers={handleChangeDeniedDrivers}
                />
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定トラック</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {allAllowedTruckIds.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未指定</Typography>
                    ) : (
                      allAllowedTruckIds.map((id) => {
                        const t = trucksOptions.find((x) => x.id === id);
                        return (
                          <Chip key={id} label={t?.name ?? `ID:${id}`} size="small" color="success" variant="outlined" />
                        );
                      })
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>NGドライバー</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {allDeniedDriverIds.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未指定</Typography>
                    ) : (
                      allDeniedDriverIds.map((id) => {
                        const d = driversOptions.find((x) => x.id === id);
                        return (
                          <Chip key={id} label={d?.name ?? `ID:${id}`} size="small" color="error" variant="outlined" />
                        );
                      })
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

/** 閲覧時: 複数伝票の和集合をマスタ順でマージし、読点区切りの1行で表示（例: 2t、4t、10t） */
const ConstraintChipRow: FC<{
  label?: string;
  values: string[];
}> = memo(({ label, values }) => {
  const uniq = [...new Set(values.filter(Boolean))];
  return (
    <Box>
      {label ? (
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
      ) : null}
      {uniq.length === 0 ? (
        <Typography variant="body2" color="text.secondary">未指定</Typography>
      ) : (
        <Typography variant="body1" fontWeight="medium">{uniq.join('、')}</Typography>
      )}
    </Box>
  );
});

export default LaneDetailBasicInfo;
